import React, { createContext, ReactNode, useCallback, use, useEffect, useMemo } from 'react';
import { useManagedPromise } from '../hooks-clean';
import ConfirmationDialog from '@components-clean/core/confirmation-dialog';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import WarningLostDataDialog from '@components-clean/legacy/unsaved-changes-dialog/warning-lost-data-dialog';
import NavigationService from '@components-clean/page-nav/navigation.service';
import { ReadOnlyContextProvider } from '@contexts-clean/read-only-context';
import { useMap, useTranslation } from '@hooks/index';

/**
 * The callback to use for creating a confirmation dialog, passing in an optional `dataTestId`.\
 * Returns a `Promise<boolean>`. If nothing is passed in, a default of `'confirmation-dialog'` is used.
 */
type UnsavedChangesDialogCallback = (dataTestId?: string) => Promise<boolean>;

/**
 * The callback for a simple OK/Cancel dialog.
 */
type OkCancelDialogCallback = (title: string, message: string, dataTestId?: string) => Promise<boolean>;

/**
 * The callback to use for creating a confirmation dialog for drag and drop components.\
 * Returns a `Promise<boolean>`, and a title and body. If no `dataTestId` is passed in, then a default of
 * `'draggable-dialog'` is used.
 */
type DraggableChangesCallback = (title: string, body: string, dataTestId?: string) => Promise<boolean>;

export interface IUnsavedChangesContext {
    /**
     * An awaitable function callback that shows a discard changes confirmation dialog, passing in an optional `dataTestId`.
     * Returns a `Promise<boolean>`. If nothing is passed in, a default of `'confirmation-dialog'` is used.
     *
     * Usage:
     * ```typescript
     * const { confirmDiscardChanges } = useUnsavedChangesContext();
     * const result = await confirmDiscardChanges('my-data-test-id'); // true/false`
     * ```
     */
    confirmDiscardChanges: UnsavedChangesDialogCallback;

    /**
     * An awaitable function callback that shows a legacy discard changes confirmation dialog, returns a `Promise<boolean>`.
     *
     * Only the changes made in Legacy CM will trigger this dialog.
     * Usage:
     * ```typescript
     * const { confirmDiscardChangesLegacy } = useUnsavedChangesContext();
     * const result = await confirmDiscardChangesLegacy(); // true/false`
     * ```
     */
    confirmDiscardChangesLegacy: UnsavedChangesDialogCallback;

    showConfirmCancelChanges: OkCancelDialogCallback;

    /**
     * Confirms that making changes on a drag and drop component will be clear filters
     */
    confirmDraggableChangesWillClearFilters: DraggableChangesCallback;

    // Individual component of a specific unsaved changes map has changes
    /**
     * Checks whether a component in the general unsaved changes map has active changes on it
     * ** DO NOT USE FOR TOOLKITS **
     * @param componentName The name of the component (or key)
     */
    componentHasChanges: (componentName: string) => boolean;
    /**
     * Checks whether a component in the Central Monitoring Toolkit unsaved changes map has active changes on it
     * @param componentName The name of the component (or key)
     */
    centralMonitoringToolkitHasChanges: (componentName: string) => boolean;
    /**
     * Checks whether a component in the Subject Monitoring Toolkit unsaved changes map has active changes on it
     * @param componentName The name of the component (or key)
     */
    subjectMonitoringToolkitHasChanges: (componentName: string) => boolean;
    /**
     * Checks whether a component in the Risk Assessment Toolkit unsaved changes map has active changes on it
     * @param componentName The name of the component (or key)
     */
    riskAssessmentToolkitHasChanges: (componentName: string) => boolean;

    // Any component of a specific unsaved changes map has changes
    /**
     * Whether any component that is being tracked has active changes
     * ** DO NOT USE FOR TOOLKITS **
     */
    anyComponentHasChanges: () => boolean;
    /**
     * Whether any component in the Central Monitoring Toolkit has changes
     */
    anyCentralMonitoringToolkitHasChanges: () => boolean;
    /**
     * Whether any component in the Subject Monitoring Toolkit has changes
     */
    anySubjectMonitoringToolkitHasChanges: () => boolean;
    /**
     * Whether any component in the Risk Assessment Toolkit has changes
     */
    anyRiskAssessmentToolkitHasChanges: () => boolean;

    // Set whether a specific component has changes on a specific unsaved changes map
    /**
     * Clears all tracked changes in the general unsaved changes map
     * ** DO NOT USE FOR TOOLKITS **
     */
    discardChanges: () => void;
    /**
     * Clears all tracked changes in the Central Monitoring Toolkit
     */
    centralMonitoringToolkitDiscardChanges: () => void;
    /**
     * Clears all tracked changes in the Subject Monitoring Toolkit
     */
    subjectMonitoringToolkitDiscardChanges: () => void;
    /**
     * Clears all tracked changes in the Risk Assessment Toolkit
     */
    riskAssessmentToolkitDiscardChanges: () => void;

    // Set whether a specific component has changes on a specific unsaved changes map
    /**
     * Sets whether the given component has changes.\
     * ** DO NOT USE FOR TOOLKITS **
     * **For toolkits, implement a separate change tracking**
     * @param key The key of the component to set
     * @param value Whether the component has changes
     */
    setComponentHasChanges: (key: string, value: boolean) => void;
    /**
     * Sets whether the given component of the Central Monitoring Toolkit has changes
     * @param key The key of the component to set
     * @param value Whether the component has changes
     */
    setCentralMonitoringToolkitHasChanges: (key: string, value: boolean) => void;
    /**
     * Sets whether the given component of the Subject Monitoring Toolkit has changes
     * @param key The key of the component to set
     * @param value Whether the component has changes
     */
    setSubjectMonitoringToolkitHasChanges: (key: string, value: boolean) => void;
    /**
     * Sets whether the given component of the Risk Assessment Toolkit has changes
     * @param key The key of the component to set
     * @param value Whether the component has changes
     */
    setRiskAssessmentToolkitHasChanges: (key: string, value: boolean) => void;
}

export const ComponentNames = {
    commentsSideBar: 'comments-side-bar',
};

/**
 * Context that provides the `IUnsavedChangesContext`
 */
const UnsavedChangesContext = createContext<IUnsavedChangesContext | null>(null);

interface UnsavedChangesProviderProps {
    children: ReactNode;
}

/**
 * Provider that tracks changes of any panel within it, setting the `IUnsavedChangesContext.changedItemsMap`
 * @param children The child React node
 */
export const UnsavedChangesProvider: React.FC<UnsavedChangesProviderProps> = ({
    children,
}: UnsavedChangesProviderProps) => {
    const [showError, setShowError] = React.useState(false);
    const [showDraggableWarning, setShowDraggableWarning] = React.useState(false);
    const [showLegacyError, setShowLegacyError] = React.useState(false);
    const [showConfirmCancelConfirmation, setShowConfirmCancelConfirmation] = React.useState(false);

    const [customConfirmationMessage, setCustomConfirmationMessage] = React.useState('');
    const [customConfirmationTitle, setCustomConfirmationTitle] = React.useState('');

    const [dialogDataTestId, setDialogDataTestId] = React.useState<string>('confirmation-dialog');
    const [draggableWarningTitle, setDraggableWarningTitle] = React.useState('');
    const [draggableWarningBody, setDraggableWarningBody] = React.useState('');
    const { t } = useTranslation();
    const confirmDiscardChangesPromise = useManagedPromise<boolean>();
    const confirmDiscardChangesLegacyPromise = useManagedPromise<boolean>();
    const confirmDraggableChangesPromise = useManagedPromise<boolean>();
    const confirmationCancelPromise = useManagedPromise<boolean>();

    const changedItemsMap = useMap<string, boolean>();
    const subjectMonitoringToolkitChangedItemsMap = useMap<string, boolean>();
    const centralMonitoringToolkitChangedItemsMap = useMap<string, boolean>();
    const riskAssessmentToolkitChangedItemsMap = useMap<string, boolean>();

    const closeAllDialogs = () => {
        setShowError(false);
        setShowLegacyError(false);
        setShowDraggableWarning(false);
        setShowConfirmCancelConfirmation(false);
    };

    const confirmDiscardChanges = useCallback<UnsavedChangesDialogCallback>(
        (dataTestId?: string) => {
            closeAllDialogs();
            setShowError(true);
            if (dataTestId) setDialogDataTestId(dataTestId);
            return confirmDiscardChangesPromise.create();
        },
        [confirmDiscardChangesPromise],
    );

    const showConfirmCancelChanges = useCallback<OkCancelDialogCallback>(
        (title: string, message: string, dataTestId?: string) => {
            closeAllDialogs();
            setShowConfirmCancelConfirmation(true);
            if (dataTestId) setDialogDataTestId(dataTestId);
            setCustomConfirmationTitle(title);
            setCustomConfirmationMessage(message);
            return confirmationCancelPromise.create();
        },
        [confirmationCancelPromise],
    );

    const confirmDiscardChangesLegacy = useCallback<UnsavedChangesDialogCallback>(() => {
        closeAllDialogs();
        setShowLegacyError(true);
        return confirmDiscardChangesLegacyPromise.create();
    }, [confirmDiscardChangesLegacyPromise]);

    const confirmDraggableChangesWillClearFilters = useCallback<DraggableChangesCallback>(
        (title: string, body: string, dataTestId?: string) => {
            closeAllDialogs();
            setDialogDataTestId(dataTestId ?? 'draggable-dialog');
            setDraggableWarningBody(body);
            setDraggableWarningTitle(title);
            setShowDraggableWarning(true);
            return confirmDraggableChangesPromise.create();
        },
        [confirmDraggableChangesPromise],
    );

    // We do not need to check if we have any changes for legacy and draggable dialog
    const hasAnyChanges = useMemo(() => {
        return changedItemsMap.value.values().some((valueHasChanged) => valueHasChanged);
    }, [changedItemsMap.value]);

    useEffect(() => {
        NavigationService.showWarningLostDataModal(hasAnyChanges);
    }, [hasAnyChanges]);

    const handleCloseConfirmCancel = (result: boolean) => {
        confirmationCancelPromise.resolve(result);
        closeAllDialogs();
    };

    const handleClose = (result: boolean) => {
        confirmDiscardChangesPromise.resolve(result);
        closeAllDialogs();
    };

    const handleCloseDraggableWarning = (result: boolean) => {
        confirmDraggableChangesPromise.resolve(result);
        closeAllDialogs();
    };

    // Individual component of a specific unsaved changes map has changes
    const componentHasChanges = useCallback(
        (componentName: string) => changedItemsMap.value.get(componentName) === true,
        [changedItemsMap.value],
    );

    const centralMonitoringToolkitHasChanges = useCallback(
        (componentName: string) => centralMonitoringToolkitChangedItemsMap.value.get(componentName) === true,
        [centralMonitoringToolkitChangedItemsMap.value],
    );

    const subjectMonitoringToolkitHasChanges = useCallback(
        (componentName: string) => subjectMonitoringToolkitChangedItemsMap.value.get(componentName) === true,
        [subjectMonitoringToolkitChangedItemsMap.value],
    );

    const riskAssessmentToolkitHasChanges = useCallback(
        (componentName: string) => riskAssessmentToolkitChangedItemsMap.value.get(componentName) === true,
        [riskAssessmentToolkitChangedItemsMap.value],
    );

    // Any component of a specific unsaved changes map has changes
    const anyComponentHasChanges = useCallback(
        () => changedItemsMap.value.values().some((valueHasChanged) => valueHasChanged),
        [changedItemsMap.value],
    );

    const anyCentralMonitoringToolkitHasChanges = useCallback(
        () => centralMonitoringToolkitChangedItemsMap.value.values().some((valueHasChanged) => valueHasChanged),
        [centralMonitoringToolkitChangedItemsMap.value],
    );

    const anySubjectMonitoringToolkitHasChanges = useCallback(
        () => subjectMonitoringToolkitChangedItemsMap.value.values().some((valueHasChanged) => valueHasChanged),
        [subjectMonitoringToolkitChangedItemsMap.value],
    );

    const anyRiskAssessmentToolkitHasChanges = useCallback(
        () => riskAssessmentToolkitChangedItemsMap.value.values().some((valueHasChanged) => valueHasChanged),
        [riskAssessmentToolkitChangedItemsMap.value],
    );

    // Discard all changes from a specific unsaved changes map
    const discardChanges = changedItemsMap.clear;
    const centralMonitoringToolkitDiscardChanges = centralMonitoringToolkitChangedItemsMap.clear;
    const subjectMonitoringToolkitDiscardChanges = subjectMonitoringToolkitChangedItemsMap.clear;
    const riskAssessmentToolkitDiscardChanges = riskAssessmentToolkitChangedItemsMap.clear;

    // Set whether a specific component has changes on a specific unsaved changes map
    const setComponentHasChanges = changedItemsMap.set;
    const setCentralMonitoringToolkitHasChanges = centralMonitoringToolkitChangedItemsMap.set;
    const setSubjectMonitoringToolkitHasChanges = subjectMonitoringToolkitChangedItemsMap.set;
    const setRiskAssessmentToolkitHasChanges = riskAssessmentToolkitChangedItemsMap.set;

    const handleCloseLegacy = (result: boolean) => {
        confirmDiscardChangesLegacyPromise.resolve(result);
        NavigationService.legacyShowWarningLostDataModal(false);
        closeAllDialogs();
    };

    const memoUnsavedChangesContext = useMemo(
        () => ({
            confirmDiscardChanges,
            confirmDiscardChangesLegacy,
            confirmDraggableChangesWillClearFilters,
            showConfirmCancelChanges,
            // Individual component of a specific unsaved changes map has changes
            componentHasChanges,
            centralMonitoringToolkitHasChanges,
            subjectMonitoringToolkitHasChanges,
            riskAssessmentToolkitHasChanges,
            // Any component of a specific unsaved changes map has changes
            anyComponentHasChanges,
            anyCentralMonitoringToolkitHasChanges,
            anySubjectMonitoringToolkitHasChanges,
            anyRiskAssessmentToolkitHasChanges,
            // Discard all changes from a specific unsaved changes map
            discardChanges,
            centralMonitoringToolkitDiscardChanges,
            subjectMonitoringToolkitDiscardChanges,
            riskAssessmentToolkitDiscardChanges,
            // Set whether a specific component has changes on a specific unsaved changes map
            setComponentHasChanges,
            setCentralMonitoringToolkitHasChanges,
            setSubjectMonitoringToolkitHasChanges,
            setRiskAssessmentToolkitHasChanges,
        }),
        [
            confirmDiscardChanges,
            confirmDiscardChangesLegacy,
            confirmDraggableChangesWillClearFilters,
            showConfirmCancelChanges,

            componentHasChanges,
            centralMonitoringToolkitHasChanges,
            subjectMonitoringToolkitHasChanges,
            riskAssessmentToolkitHasChanges,

            anyComponentHasChanges,
            anyCentralMonitoringToolkitHasChanges,
            anySubjectMonitoringToolkitHasChanges,
            anyRiskAssessmentToolkitHasChanges,

            discardChanges,
            centralMonitoringToolkitDiscardChanges,
            subjectMonitoringToolkitDiscardChanges,
            riskAssessmentToolkitDiscardChanges,

            setComponentHasChanges,
            setCentralMonitoringToolkitHasChanges,
            setSubjectMonitoringToolkitHasChanges,
            setRiskAssessmentToolkitHasChanges,
        ],
    );

    return (
        <UnsavedChangesContext value={memoUnsavedChangesContext}>
            <ReadOnlyContextProvider>
                {children}
                {showConfirmCancelConfirmation && (
                    <ConfirmationDialog
                        title={customConfirmationTitle}
                        dataTestId={dialogDataTestId}
                        onSubmit={() => handleCloseConfirmCancel(true)}
                        onClose={() => handleCloseConfirmCancel(false)}
                        primaryBtnLabel={t('confirm')}
                        closeIcon
                        style={{ zIndex: 2147483647 }}
                        secondaryBtnLabel={t('cancel')}
                    >
                        {customConfirmationMessage}
                    </ConfirmationDialog>
                )}
                {showError && !showLegacyError && (
                    <ConfirmationDialog
                        title={t('unsavedData')}
                        dataTestId={dialogDataTestId}
                        onSubmit={() => handleClose(true)}
                        onClose={() => handleClose(false)}
                        primaryBtnLabel={t('continue')}
                        closeIcon
                        style={{ zIndex: 2147483647 }}
                    >
                        {t('unsavedChanges')} {t('unsavedChangesLost')}
                    </ConfirmationDialog>
                )}
                {showLegacyError && !showError && (
                    <WarningLostDataDialog
                        open={showLegacyError}
                        onClose={(action: string) => {
                            if (action === 'close') {
                                handleCloseLegacy(true);
                            } else {
                                setShowLegacyError(false);
                            }
                        }}
                    />
                )}
                {showDraggableWarning && (
                    <DraggableDialog
                        dataTestId={dialogDataTestId}
                        onSubmit={() => handleCloseDraggableWarning(true)}
                        onClose={() => handleCloseDraggableWarning(false)}
                        title={draggableWarningTitle}
                        width={575}
                    >
                        {draggableWarningBody}
                    </DraggableDialog>
                )}
            </ReadOnlyContextProvider>
        </UnsavedChangesContext>
    );
};

/**
 * Hook to retrieve the `IUnsavedChangesContext`
 */
export function useUnsavedChangesContext(): IUnsavedChangesContext {
    const context = use(UnsavedChangesContext);
    if (!context) {
        throw new Error('useUnsavedChangesContext must be used within a UnsavedChangesProvider');
    }
    return context;
}
